import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

const MAINTENANCE_ROUTE = '/authentication/maintenance';

/**
 * Redirects to the maintenance page when the backend returns the maintenance
 * marker (HTTP 503 + `X-Site-Maintenance: 1`, or body `{ code: 'SITE_MAINTENANCE' }`).
 *
 * This complements MaintenanceGuard, which only fires on Angular navigation:
 * a logged-in user who stays on the same page keeps a usable UI until they
 * trigger an HTTP call. The hook on the backend returns 503 on every action,
 * and this interceptor sends the user to the maintenance page on the next
 * such error.
 *
 * Registered LAST in the providers array on purpose: Angular runs response
 * handlers in reverse-registration order, so being last makes this the first
 * to see the error and short-circuits the redirect before any other
 * interceptor reacts to it.
 */
@Injectable()
export class MaintenanceInterceptor implements HttpInterceptor {
  constructor(private router: Router) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.isMaintenanceError(error)) {
          this.redirectToMaintenance();
        }
        return throwError(() => error);
      })
    );
  }

  private isMaintenanceError(error: HttpErrorResponse): boolean {
    if (!error || error.status !== 503) {
      return false;
    }
    const headerFlag = error.headers?.get?.('X-Site-Maintenance');
    if (headerFlag === '1') {
      return true;
    }
    const body: any = error.error;
    return !!body && body.code === 'SITE_MAINTENANCE';
  }

  private redirectToMaintenance(): void {
    const currentUrl = (this.router.url || '').split('?')[0];
    if (
      currentUrl === MAINTENANCE_ROUTE ||
      currentUrl.startsWith(`${MAINTENANCE_ROUTE}/`)
    ) {
      return;
    }
    void this.router.navigateByUrl(MAINTENANCE_ROUTE);
  }
}
